/* eslint-disable no-unused-vars */
import React, {useEffect, useState, useRef, useMemo, useCallback} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, KTCardHeader, Loading} from '../../../components'
import {PageTitle} from '../../../core'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import listPlugin from '@fullcalendar/list'
import trLocale from '@fullcalendar/core/locales/tr'
import tippy from 'tippy.js'
import 'tippy.js/dist/tippy.css'
import 'tippy.js/themes/light.css'

import {useDispatch, useSelector} from 'react-redux'
import {taskCalendarGetAll} from '../../../redux/slices/task-calendar.slice'

import {
  useMemoizedStaticData,
  useMemoizedHandlers
} from '../../../utils/performanceOptimizations'

const TASK_COLOR = '#14B8A6'

const selectTaskCalendarData = (state) => ({
  calendar: state.taskCalendar.calendar,
  loading: state.taskCalendar.loading
})

function WorkCalendar() {
  const intl = useIntl()

  const {calendar, loading} = useSelector(selectTaskCalendarData)
  const dispatch = useDispatch()

  const initialDates = useMemo(() => {
    const now = new Date()
    const currentYear = now.getFullYear()
    const currentMonth = now.getMonth()

    return {
      startDate: new Date(currentYear, currentMonth, 1),
      endDate: new Date(currentYear, currentMonth + 1, 0)
    }
  }, [])

  const [startDate, setStartDate] = useState(initialDates.startDate)
  const [endDate, setEndDate] = useState(initialDates.endDate)

  const calendarRef = useRef(null)

  useEffect(() => {
    dispatch(taskCalendarGetAll({startDate, endDate}))
  }, [dispatch, startDate, endDate])

  const calendarPlugins = useMemoizedStaticData(() => [
    dayGridPlugin,
    timeGridPlugin,
    listPlugin
  ], [])

  const headerToolbar = useMemoizedStaticData(() => ({
    left: 'title',
    center: '',
    right: 'dayGridMonth,timeGridWeek,timeGridDay',
  }), [])

  const handlers = useMemoizedHandlers({
    goNext: () => {
      const calendarApi = calendarRef.current.getApi()
      calendarApi.next()
      const currentStartDate = new Date(startDate)
      const nextMonth = new Date(currentStartDate.setMonth(currentStartDate.getMonth() + 1))
      setStartDate(new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 1))
      setEndDate(new Date(nextMonth.getFullYear(), nextMonth.getMonth() + 1, 0))
    },
    goPrev: () => {
      const calendarApi = calendarRef.current.getApi()
      calendarApi.prev()
      const currentStartDate = new Date(startDate)
      const prevMonth = new Date(currentStartDate.setMonth(currentStartDate.getMonth() - 1))
      setStartDate(new Date(prevMonth.getFullYear(), prevMonth.getMonth(), 1))
      setEndDate(new Date(prevMonth.getFullYear(), prevMonth.getMonth() + 1, 0))
    },
    handleEventClick: (event) => {
      event.preventDefault()
    }
  }, [startDate])

  const handleEventDidMount = useCallback((info) => {
    const tooltip = document.createElement('div')
    tooltip.innerHTML = `
      <div class='p-3 w-250px d-flex flex-column gap-2'>
        <div class='fw-bold text-dark'>${info.event.title}</div>
        <div class='text-muted fw-semibold'>${info.event.extendedProps.description || ''}</div>
        <a class='btn btn-sm btn-primary mt-2' href="${info.event.url}" target="_blank" rel="noopener noreferrer">${intl.formatMessage({id: 'BUTTON.DETAIL'})}</a>
      </div>
    `
    tippy(info.el, {
      content: tooltip,
      placement: 'top',
      trigger: 'mouseenter',
      interactive: true,
      allowHTML: true,
      theme: 'light',
    })
  }, [intl])

  const calendarEvents = useMemo(() => {
    if (!calendar) return []

    return calendar.map((event) => ({
      id: event.id,
      title: event.sourceTypeName + ' - ' + event.title,
      date: (() => { const d = new Date(event.startDate); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` })(),
      description: event.description,
      url: event.url,
      backgroundColor: TASK_COLOR,
      borderColor: TASK_COLOR,
      extendedProps: {
        sourceTypeId: event.sourceTypeId,
        sourceTypeName: event.sourceTypeName,
        description: event.description,
      },
    }))
  }, [calendar])

  const pageTitle = useMemo(() =>
    intl.formatMessage({id: 'MENU.WORKPLAN.WORKCALENDAR'}),
    [intl]
  )

  return (
    <>
      <PageTitle>{pageTitle}</PageTitle>
      <KTCard>
        <KTCardHeader className='fc fc-media-screen fc-direction-ltr fc-theme-standard'>
          <div className='card-toolbar fc-header-toolbar fc-toolbar fc-toolbar-ltr justify-content-end'>
            <div className='fc-toolbar-chunk'>
              <div className='fc-button-group'>
                <button
                  type='button'
                  title='Previous month'
                  aria-pressed='false'
                  className='fc-prev-button fc-button fc-button-primary'
                  onClick={handlers.goPrev}
                >
                  <span className='fc-icon fc-icon-chevron-left'></span>
                </button>
                <button
                  type='button'
                  title='Next month'
                  aria-pressed='false'
                  className='fc-next-button fc-button fc-button-primary'
                  onClick={handlers.goNext}
                >
                  <span className='fc-icon fc-icon-chevron-right'></span>
                </button>
              </div>
            </div>
          </div>
        </KTCardHeader>
        <KTCardBody>
          <FullCalendar
            ref={calendarRef}
            plugins={calendarPlugins}
            initialView='dayGridMonth'
            locale={trLocale}
            eventDidMount={handleEventDidMount}
            eventClick={handlers.handleEventClick}
            events={calendarEvents}
            headerToolbar={headerToolbar}
            dayMaxEvents={5}
          />
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}

export {WorkCalendar}
